// @flow
import { addParamsToUrl } from '@zapier/url-utils';

import getCookie from './getCookie';

const POPUP_SPLASH_URL = '/window-splash/';
const POPUP_WINDOW_OPTIONS = 'height=700,width=980,scrollbars=yes';

// Create the popup window and return the new window object ref.
// If the window cannot be created (is blocked) then null is returned.
// See https://developer.mozilla.org/en-US/docs/Web/API/Window/open#Return_value
export const createPopupWindow = (url: string): * =>
  window.open(url, '_blank', POPUP_WINDOW_OPTIONS);

// Decorate the original url with a /csrf/fix view/url that will set the missing csrf
// token. This is a useful workaround for Safari's security policy that block cookies
// in iframes if the user has never visited the site outside of an iframe.
//
// Note: The backend requires the `next` url to be strictly the POPUP_SPLASH_URL based
// on security concerns.
const maybeCsrfFix = url =>
  !getCookie('csrftoken') ? addParamsToUrl('/csrf/fix/', { next: url }) : url;

const createPopupSplash = (): Promise<*> =>
  new Promise((resolve, reject) => {
    const { origin } = window.location;
    const popup = createPopupWindow(maybeCsrfFix(POPUP_SPLASH_URL));

    // When popups are blocked by the browser `popup` will be falsey
    // so we return early here.
    if (!popup) {
      reject(new Error('Popup was blocked.'));
      return;
    }

    // This code will setup a postMessage listener that will listen for an event
    // with the data payload of `ready`. Assuming that the origins match, this
    // will then trigger the resolve of the `popup` which will eventually be used
    // to redirect the popup to the desired authentication URL.
    // This code exists for cross-browser purposes, binding to the `popup`
    // `DOMContentLoaded` or `load` event will not work in all browsers.
    const onPostMessageAuthPopUpReady = event => {
      if (event.origin === origin && event.data === 'ready') {
        window.removeEventListener('message', onPostMessageAuthPopUpReady);

        resolve(popup);
      }
    };

    window.addEventListener('message', onPostMessageAuthPopUpReady, false);
  });

export default createPopupSplash;
